import { PageHeader } from '../components/ui/PageHeader';
import { RowToggle } from '../components/ui/RowToggle';
import { ActionBar } from '../components/ui/ActionBar';
import { Icon } from '../components/ui/Icon';
import { Button } from '../components/ui/primitives';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { getStores } from '../api/stores';
import {
  assignUserStores,
  createUser,
  getUsers,
  getUserStores,
  resetUserPassword,
  setUserActive,
  updateUser,
} from '../api/users';
import { getErrorMessage } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { canManageTargetUser } from '../auth/permissions';
import { Modal } from '../components/Modal';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { roleLabel } from '../components/BusinessUi';
import { getLoyverseCatalogEmployees } from '../api/loyverse';
import type { LoyverseCatalogEmployee } from '../types/loyverse';
import type { Store } from '../types/stores';
import type { User } from '../types/users';

type RegularRole = 'MANAGER' | 'EMPLOYEE';

interface UserFormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  username: string;
  role: RegularRole;
  password: string;
  storeIds: string[];
  loyverseEmployeeId: string;
}

const emptyUserForm: UserFormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  username: '',
  role: 'EMPLOYEE',
  password: '',
  storeIds: [],
  loyverseEmployeeId: '',
};

export function UsersPage() {
  const confirm = useConfirm();
  const { user: actor } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState<string>();
  const [success, setSuccess] = useState<string>();
  const [editingUser, setEditingUser] = useState<User | null>(null);
  // Empleados del POS sincronizados; si Loyverse no está conectado la lista queda vacía y el selector no aparece.
  const [loyverseEmployees, setLoyverseEmployees] = useState<LoyverseCatalogEmployee[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState<UserFormState>(emptyUserForm);
  const [formError, setFormError] = useState<string>();
  const [isSaving, setIsSaving] = useState(false);
  const [busyUserId, setBusyUserId] = useState<string>();
  // Pre-client UX hardening (móvil): tarjetas colapsables en vez de una tabla de 940 px con scroll
  // lateral invisible (mismo patrón que Compras/Gastos/Cortes). Cero cambios de permisos.
  const [expandedUserIds, setExpandedUserIds] = useState<Set<string>>(new Set());
  const toggleUserExpanded = (id: string) => setExpandedUserIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const [temporaryCredential, setTemporaryCredential] = useState<{ username: string; password: string }>();
  const [assignmentUser, setAssignmentUser] = useState<User | null>();
  const [assignmentStoreIds, setAssignmentStoreIds] = useState<string[]>([]);
  const [assignmentImplicit, setAssignmentImplicit] = useState(false);
  const [assignmentError, setAssignmentError] = useState<string>();
  const [isAssignmentLoading, setIsAssignmentLoading] = useState(false);
  const [isAssignmentSaving, setIsAssignmentSaving] = useState(false);

  const loadData = useCallback(async () => {
    setPageError(undefined);
    setIsLoading(true);

    try {
      const [usersResult, storesResult] = await Promise.all([getUsers(), getStores()]);
      setUsers(usersResult.users);
      setStores(storesResult.stores);
    } catch (error) {
      setPageError(getErrorMessage(error, 'No fue posible cargar los usuarios.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { getLoyverseCatalogEmployees().then((result) => setLoyverseEmployees(result.employees)).catch(() => {}); }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const canChooseManagerRole = actor?.role === 'OWNER';

  const openCreate = () => {
    setEditingUser(null);
    setForm(emptyUserForm);
    setFormError(undefined);
    setIsCreating(true);
  };

  const openEdit = (target: User) => {
    if (target.role === 'OWNER' || !canManageTargetUser(actor?.role, target.role)) return;
    setIsCreating(false);
    setEditingUser(target);
    setForm({
      firstName: target.firstName || '',
      lastName: target.lastName || '',
      email: target.email,
      phone: target.phone || '',
      username: target.username,
      role: target.role,
      password: '',
      storeIds: target.stores.filter((store) => store.isActive).map((store) => store.id),
      loyverseEmployeeId: target.loyverseEmployeeId || '',
    });
    setFormError(undefined);
  };

  const closeForm = () => {
    if (isSaving) return;
    setEditingUser(null);
    setIsCreating(false);
    setFormError(undefined);
  };

  const handleFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSaving) return;

    const validationError = validateUserForm(form);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setFormError(undefined);
    setIsSaving(true);

    try {
      const commonInput = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        username: form.username.trim(),
        role: form.role,
        storeIds: form.storeIds,
        ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
        ...(form.password ? { password: form.password } : {}),
      };

      if (editingUser) {
        const result = await updateUser(editingUser.id, {
          ...commonInput,
          phone: form.phone.trim() || null,
          loyverseEmployeeId: form.loyverseEmployeeId || null,
        });
        replaceUser(result.user);
        setSuccess(`Usuario ${result.user.username} actualizado.`);
      } else {
        const result = await createUser(commonInput);
        setUsers((current) => [...current, result.user]);
        setSuccess(`Usuario ${result.user.username} creado.`);
        if (result.temporaryPassword) {
          setTemporaryCredential({
            username: result.user.username,
            password: result.temporaryPassword,
          });
        }
      }

      setEditingUser(null);
      setIsCreating(false);
    } catch (error) {
      setFormError(getErrorMessage(error, 'No fue posible guardar el usuario.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleActiveChange = async (target: User) => {
    if (!canManageTargetUser(actor?.role, target.role) || busyUserId) return;
    const nextActive = !target.isActive;

    if (!nextActive && !(await confirm({ title: 'Desactivar usuario', description: `¿Desactivar a ${target.username}? Sus sesiones activas se cerrarán.`, confirmLabel: 'Desactivar', danger: true }))) {
      return;
    }

    setBusyUserId(target.id);
    setPageError(undefined);
    setSuccess(undefined);

    try {
      const result = await setUserActive(target.id, nextActive);
      replaceUser(result.user);
      setSuccess(`Usuario ${nextActive ? 'activado' : 'desactivado'} correctamente.`);
    } catch (error) {
      setPageError(getErrorMessage(error, 'No fue posible cambiar el estado del usuario.'));
    } finally {
      setBusyUserId(undefined);
    }
  };

  const handleResetPassword = async (target: User) => {
    if (!canManageTargetUser(actor?.role, target.role) || busyUserId) return;
    if (!(await confirm({ title: 'Nueva contraseña temporal', description: `¿Generar una contraseña temporal nueva para ${target.username}?`, confirmLabel: 'Generar' }))) return;

    setBusyUserId(target.id);
    setPageError(undefined);
    setSuccess(undefined);

    try {
      const result = await resetUserPassword(target.id);
      replaceUser(result.user);
      setTemporaryCredential({ username: target.username, password: result.temporaryPassword });
      setSuccess('Contraseña temporal generada.');
    } catch (error) {
      setPageError(getErrorMessage(error, 'No fue posible resetear la contraseña.'));
    } finally {
      setBusyUserId(undefined);
    }
  };

  const openAssignments = async (target: User) => {
    setAssignmentUser(target);
    setAssignmentStoreIds([]);
    setAssignmentImplicit(false);
    setAssignmentError(undefined);
    setIsAssignmentLoading(true);

    try {
      const result = await getUserStores(target.id);
      setAssignmentStoreIds(result.stores.map((store) => store.id));
      setAssignmentImplicit(result.implicit);
    } catch (error) {
      setAssignmentError(getErrorMessage(error, 'No fue posible consultar las tiendas.'));
    } finally {
      setIsAssignmentLoading(false);
    }
  };

  const saveAssignments = async () => {
    if (!assignmentUser || assignmentImplicit || isAssignmentSaving) return;
    if (assignmentStoreIds.length === 0) {
      setAssignmentError('Selecciona al menos una tienda activa.');
      return;
    }

    setAssignmentError(undefined);
    setIsAssignmentSaving(true);

    try {
      const result = await assignUserStores(assignmentUser.id, assignmentStoreIds);
      setUsers((current) => current.map((item) => (
        item.id === assignmentUser.id ? { ...item, stores: result.stores } : item
      )));
      setSuccess(`Tiendas de ${assignmentUser.username} actualizadas.`);
      setAssignmentUser(null);
    } catch (error) {
      setAssignmentError(getErrorMessage(error, 'No fue posible asignar las tiendas.'));
    } finally {
      setIsAssignmentSaving(false);
    }
  };

  const replaceUser = (updatedUser: User) => {
    setUsers((current) => current.map((item) => item.id === updatedUser.id ? updatedUser : item));
  };

  const activeStores = stores.filter((store) => store.isActive);
  const showForm = isCreating || editingUser !== null;

  return (
    <div className="admin-page n3-screen">
      {/* P-075: el título es el del menú («Usuarios»), y un solo encabezado con su acción. */}
      <PageHeader
        actions={<Button disabled={isLoading || activeStores.length === 0} iconStart={<Icon name="plus" size={18} />} onClick={openCreate} variant="primary">Nuevo usuario</Button>}
        description="Accesos, roles y tiendas asignadas de tu equipo."
        title="Usuarios"
      />

      {pageError && <div className="alert alert--error">{pageError}</div>}
      {success && <div className="alert alert--success">{success}</div>}
      {/* P-078 (BK30): el camino que lo resuelve, a un toque (solo el dueño administra tiendas). */}
      {!isLoading && activeStores.length === 0 && (
        <div className="alert alert--warning return-banner"><span>Necesitas al menos una tienda activa para crear o editar usuarios.</span>{actor?.role === 'OWNER' && <a className="text-button" href="#stores">Ir a Tiendas →</a>}</div>
      )}

      <section className="admin-card">
        <div className="admin-card__summary">
          <span><strong>{users.length}</strong> usuarios</span>
          <span><strong>{users.filter((item) => item.isActive).length}</strong> activos</span>
        </div>

        {isLoading ? (
          <div className="inline-loading"><span className="spinner" />Cargando usuarios...</div>
        ) : users.length === 0 ? (
          <div className="empty-panel"><strong>No hay usuarios</strong><span>Crea el primer usuario operativo para comenzar.</span></div>
        ) : (
          <div className="data-table-wrap">
            <table className="data-table data-table--cards n3-has-row-toggle">
              <thead><tr><th>Usuario</th><th>Nombre</th><th>Rol</th><th>Tiendas</th><th>Estado</th><th>Acciones</th></tr></thead>
              <tbody>
                {users.map((item) => {
                  // El Dueño nunca se administra desde esta pantalla: canManageTargetUser da `true`
                  // para cualquier fila cuando el actor es OWNER (incluida otra fila OWNER), pero
                  // openEdit/el backend rechazan un objetivo OWNER — sin este `&&` los botones se
                  // veían habilitados y fallaban o no hacían nada al usarlos.
                  const canManage = canManageTargetUser(actor?.role, item.role) && item.role !== 'OWNER';
                  const isBusy = busyUserId === item.id;
                  return (
                    <tr className={`${!item.isActive ? 'data-row--inactive' : ''} ${expandedUserIds.has(item.id) ? 'dt-card--open' : 'dt-card--closed'}`} onClick={() => toggleUserExpanded(item.id)} key={item.id}>
                      <td className="dt-card-main"><strong>{item.username}</strong><small>{item.fullName || [item.firstName, item.lastName].filter(Boolean).join(' ') || item.email}</small><RowToggle expanded={expandedUserIds.has(item.id)} label={item.username} onToggle={() => toggleUserExpanded(item.id)} /></td>
                      {/* Deliberadamente NO usa userDisplayName: aqui el ultimo escalon es «Sin nombre», no el
                          usuario, porque el usuario ya se muestra en la columna de al lado y este dato existe justo para
                          decirle al administrador si esa persona tiene nombre capturado o no. */}
                      <td data-label="Nombre">{item.fullName || [item.firstName, item.lastName].filter(Boolean).join(' ') || 'Sin nombre'}{item.loyverseEmployeeName && <small>Loyverse: {item.loyverseEmployeeName}</small>}<small>{item.email}</small></td>
                      <td className="dt-card-keep" data-label="Rol"><span className={`role-pill role-pill--${item.role.toLowerCase()}`}>{roleLabel(item.role)}</span></td>
                      <td data-label="Tiendas">{item.role === 'OWNER' ? 'Todas' : item.stores.length}</td>
                      <td className="dt-card-keep" data-label="Estado"><span className={`status-pill ${item.isActive ? '' : 'status-pill--inactive'}`}>{item.isActive ? 'Activo' : 'Inactivo'}</span></td>
                      <td className="dt-card-actions dt-card-keep">
                        {/* P-076: las cuatro acciones siguen a la vista, con objetivo táctil; las sensibles (restablecer
                            la contraseña, desactivar) van aparte. Nada se mueve a un menú. */}
                        <div onClick={(event) => event.stopPropagation()}>
                          <ActionBar
                            aria-label={`Acciones de ${item.username}`}
                            danger={canManage ? <>
                              <Button disabled={isBusy} onClick={() => void handleResetPassword(item)} variant="ghost">Restablecer contraseña</Button>
                              <Button disabled={isBusy} onClick={() => void handleActiveChange(item)} variant={item.isActive ? 'danger' : 'ghost'}>{isBusy ? 'Procesando...' : item.isActive ? 'Desactivar' : 'Activar'}</Button>
                            </> : undefined}
                            note={canManage ? undefined : <span title={item.role === 'OWNER' ? 'El Dueño no se puede modificar desde aquí.' : 'No puedes modificar a un usuario con este rol.'}>Sin acciones disponibles</span>}
                          >
                            <Button onClick={() => void openAssignments(item)} variant="ghost">Tiendas asignadas</Button>
                            {canManage && <Button onClick={() => openEdit(item)} variant="ghost">Editar</Button>}
                          </ActionBar>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showForm && (
        <Modal
          description={editingUser ? 'Actualiza los datos y accesos del usuario.' : 'El rol Dueño se protege y no puede crearse desde este formulario.'}
          isBusy={isSaving}
          onClose={closeForm}
          title={editingUser ? `Editar ${editingUser.username}` : 'Crear usuario'}
          footer={<><button className="button button--ghost" disabled={isSaving} onClick={closeForm} type="button">Cancelar</button><button className="button button--primary" disabled={isSaving} form="user-form" type="submit">{isSaving ? 'Guardando...' : 'Guardar usuario'}</button></>}
        >
          <form id="user-form" onSubmit={handleFormSubmit}>
            {formError && <div className="alert alert--error">{formError}</div>}
            <div className="form-grid form-grid--compact">
              <label className="field"><span>Nombre</span><input maxLength={100} onChange={(event) => setForm({ ...form, firstName: event.target.value })} required value={form.firstName} /></label>
              <label className="field"><span>Apellido</span><input maxLength={100} onChange={(event) => setForm({ ...form, lastName: event.target.value })} required value={form.lastName} /></label>
              <label className="field"><span>Email</span><input onChange={(event) => setForm({ ...form, email: event.target.value })} required type="email" value={form.email} /></label>
              <label className="field"><span>Teléfono <small>opcional</small></span><input maxLength={30} onChange={(event) => setForm({ ...form, phone: event.target.value })} value={form.phone} /></label>
              <label className="field"><span>Nombre de usuario</span><input onChange={(event) => setForm({ ...form, username: event.target.value })} required value={form.username} /></label>
              <label className="field">
                <span>Rol</span>
                <select disabled={!canChooseManagerRole} onChange={(event) => setForm({ ...form, role: event.target.value as RegularRole })} value={form.role}>
                  {canChooseManagerRole && <option value="MANAGER">Gerente</option>}
                  <option value="EMPLOYEE">Empleado</option>
                </select>
                {!canChooseManagerRole && <small>Solo el Dueño puede crear o ascender a Gerente.</small>}
              </label>
              {editingUser && loyverseEmployees.length > 0 && <label className="field"><span>Empleado en Loyverse <small>vincula quién cobra en Loyverse</small></span><select onChange={(event) => setForm({ ...form, loyverseEmployeeId: event.target.value })} value={form.loyverseEmployeeId}><option value="">Sin vínculo</option>{loyverseEmployees.map((employee) => <option key={employee.loyverseId} value={employee.loyverseId}>{employee.name}{employee.isActive ? '' : ' (inactivo)'}</option>)}</select></label>}
              <label className="field form-grid__full"><span>Contraseña <small>{editingUser ? 'dejar vacía para conservar' : 'opcional; se genera si queda vacía'}</small></span><input inputMode="numeric" maxLength={8} onChange={(event) => setForm({ ...form, password: event.target.value })} pattern="[0-9]{4,8}" type="password" value={form.password} /></label>
            </div>
            <fieldset className="checkbox-fieldset">
              <legend>Tiendas asignadas <small>selecciona al menos una</small></legend>
              <StoreCheckboxes selectedIds={form.storeIds} stores={activeStores} onToggle={(storeId) => setForm({ ...form, storeIds: toggleId(form.storeIds, storeId) })} />
            </fieldset>
          </form>
        </Modal>
      )}

      {assignmentUser && (() => {
        const canManageAssignment = canManageTargetUser(actor?.role, assignmentUser.role);
        return (
        <Modal
          description={assignmentImplicit ? 'El Dueño tiene acceso a todas las tiendas.' : canManageAssignment ? 'Selecciona las tiendas operativas del usuario.' : 'No puedes modificar las tiendas de un usuario con este rol. Solo puedes consultarlas.'}
          isBusy={isAssignmentSaving}
          onClose={() => !isAssignmentSaving && setAssignmentUser(null)}
          title={`Tiendas de ${assignmentUser.username}`}
          footer={!assignmentImplicit && !isAssignmentLoading && canManageAssignment ? <><button className="button button--ghost" onClick={() => setAssignmentUser(null)} type="button">Cancelar</button><button className="button button--primary" disabled={isAssignmentSaving} onClick={() => void saveAssignments()} type="button">{isAssignmentSaving ? 'Guardando...' : 'Guardar asignación'}</button></> : undefined}
        >
          {assignmentError && <div className="alert alert--error">{assignmentError}</div>}
          {isAssignmentLoading ? <div className="inline-loading"><span className="spinner" />Consultando tiendas...</div> : (
            <fieldset className="checkbox-fieldset checkbox-fieldset--plain" disabled={assignmentImplicit || !canManageAssignment}>
              <StoreCheckboxes selectedIds={assignmentStoreIds} stores={assignmentImplicit ? stores : activeStores} onToggle={(storeId) => setAssignmentStoreIds(toggleId(assignmentStoreIds, storeId))} />
            </fieldset>
          )}
        </Modal>
        );
      })()}

      {temporaryCredential && (
        <Modal onClose={() => setTemporaryCredential(undefined)} title="Contraseña temporal" description="Se mostrará una sola vez. Compártela por un canal seguro.">
          <div className="temporary-password">
            <span>Usuario: <strong>{temporaryCredential.username}</strong></span>
            <code>{temporaryCredential.password}</code>
            <button className="button button--primary button--wide" onClick={() => setTemporaryCredential(undefined)} type="button">Ya la guardé</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function StoreCheckboxes({ stores, selectedIds, onToggle }: { stores: Store[]; selectedIds: string[]; onToggle: (storeId: string) => void }) {
  if (stores.length === 0) return <div className="empty-panel empty-panel--small">No hay tiendas disponibles.</div>;

  return (
    <div className="checkbox-list">
      {stores.map((store) => (
        <label className={`checkbox-option ${!store.isActive ? 'checkbox-option--inactive' : ''}`} key={store.id}>
          <input checked={selectedIds.includes(store.id)} onChange={() => onToggle(store.id)} type="checkbox" />
          <span><strong>{store.name}</strong><small>{[store.city, store.region].filter(Boolean).join(', ') || store.timezone}</small></span>
        </label>
      ))}
    </div>
  );
}

function validateUserForm(form: UserFormState): string | undefined {
  if (!form.firstName.trim() || !form.lastName.trim()) return 'Nombre y apellido son requeridos.';
  if (!form.username.trim()) return 'El nombre de usuario es requerido.';
  if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return 'Ingresa un email válido.';
  if (form.password && !/^\d{4,8}$/.test(form.password)) return 'La contraseña debe tener entre 4 y 8 dígitos.';
  if (form.storeIds.length === 0) return 'Selecciona al menos una tienda.';
  return undefined;
}

function toggleId(currentIds: string[], id: string): string[] {
  return currentIds.includes(id) ? currentIds.filter((currentId) => currentId !== id) : [...currentIds, id];
}
